import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  RACE_OPTIONS,
  ETHNICITY_OPTIONS,
  LANGUAGE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RELATIONSHIP_OPTIONS,
} from "../utils/constants";
import {
  Stack,
  Card,
  Title,
  Text,
  SimpleGrid,
  Divider,
  Group,
  Button,
  Loader,
  Alert,
} from "@mantine/core";
import { IconHelp, IconAlertCircle } from "@tabler/icons-react";
import { FaqModal } from "./FAQModal";
import { compressImageToDataUrl } from "../utils/utils";
import "../EventForm.css";

// pdf.js is large, so only load it when the patient reaches the consent step
const ConsentPdfSigner = lazy(() => import("./ConsentPdfSigner"));

// Photos are compressed before upload, so the selected file can be larger
// than what is actually sent
const MAX_INSURANCE_CARD_FILE_SIZE_BYTES = 25 * 1024 * 1024;
// Uploading insurance card photos on slow connections needs longer than the
// default API timeout
const SUBMIT_TIMEOUT_MS = 120000;
const ALLOWED_INSURANCE_CARD_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

const CONSENT_FORMS = [
  {
    formType: "hipaaPrivacy",
    title: "HIPAA Privacy and Release of Information",
  },
  {
    formType: "consentToCare",
    title: "Consent to Receive Care",
  },
];

const getConsentTemplateUrl = (formType) =>
  `${api.defaults.baseURL}/consentForms/${formType}/template`;

// Must match the backend's CONSENT_FORM_TIME_ZONE so the previewed date is
// the date that ends up on the signed PDF.
const CONSENT_SIGNING_TIME_ZONE = "America/Chicago";

const formatDateOnlyForForm = (dateOnly) => {
  const match = String(dateOnly || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : "";
};

const getSubmissionErrorMessage = (err) => {
  if (err?.response?.status === 413) {
    return "The insurance card images are too large to upload. Please choose smaller photos and try again.";
  }
  if (err?.code === "ECONNABORTED") {
    return "The upload took too long, which usually means a slow connection. Please check with the event staff before trying again, as your registration may have gone through.";
  }
  if (!err?.response) {
    return "Unable to reach the server. Please check your connection and try again.";
  }
  return err.response.data?.message || "Registration failed. Please try again.";
};

// Patients under this age need a parent or guardian to sign the consent forms
const CONSENT_GUARDIAN_AGE = 16;

// Age in whole years as of today in the signing time zone, or null if the
// date of birth is missing or malformed.
const getAgeOnSigningDate = (dateOnly) => {
  const dob = String(dateOnly || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dob) return null;
  const [month, day, year] = new Date()
    .toLocaleDateString("en-US", {
      timeZone: CONSENT_SIGNING_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .map(Number);
  const age = year - Number(dob[1]);
  const hadBirthday =
    month > Number(dob[2]) ||
    (month === Number(dob[2]) && day >= Number(dob[3]));
  return hadBirthday ? age : age - 1;
};

const createInitialConsents = () =>
  Object.fromEntries(
    CONSENT_FORMS.map(({ formType }) => [
      formType,
      { agreed: false, signature: null },
    ])
  );

const initialFormData = {
  // Basic Personal Information
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: "",
  ssn: "",
  sex: "",
  race: "",
  raceDeclined: false,
  ethnicity: "",
  ethnicityDeclined: false,
  language: "",
  maritalStatus: "",
  // Address Information
  streetAddress: "",
  streetAddress2: "",
  city: "",
  state: "",
  zipCode: "",
  phoneNumber: "",
  email: "",
  // Organization Information
  organization: "",
  titleRole: "",
  // Policy Information
  insuranceName: "",
  relationshipToPolicyHolder: "",
  memberID: "",
  policyGroupNumber: "",
  issueDate: "",
  expiration: "",
  // Policy Holder Information
  entityType: "person",
  policyHolderFirstName: "",
  policyHolderLastName: "",
  policyHolderAddress: "",
  policyHolderSex: "",
};

const initialInsuranceCardFiles = {
  front: null,
  back: null,
};

const PatientForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ ...initialFormData });
  const [step, setStep] = useState(1); // 1 = Personal, 2 = Insurance, 3 = Review, 4 = Consent Forms
  const [countdown, setCountdown] = useState(10); // 10 second countdown
  const [faqModalOpened, setFaqModalOpened] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [insuranceCardFiles, setInsuranceCardFiles] = useState({
    ...initialInsuranceCardFiles,
  });
  const [consents, setConsents] = useState(createInitialConsents);

  // Values pre-filled into the consent PDFs' form fields
  const consentFieldValues = useMemo(
    () => ({
      First: formData.firstName,
      Last: formData.lastName,
      Language: formData.language,
      DOB: formatDateOnlyForForm(formData.dateOfBirth),
      full_name: [formData.firstName, formData.middleName, formData.lastName]
        .filter(Boolean)
        .join(" "),
      date: new Date().toLocaleDateString("en-US", {
        timeZone: CONSENT_SIGNING_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    }),
    [
      formData.firstName,
      formData.middleName,
      formData.lastName,
      formData.language,
      formData.dateOfBirth,
    ]
  );

  const patientAge = getAgeOnSigningDate(formData.dateOfBirth);
  const needsGuardianSignature =
    patientAge !== null && patientAge < CONSENT_GUARDIAN_AGE;

  useEffect(() => {
    fetchEvent();
  }, []);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await api.get("/eventInfo"); // Axios instance
      setEventData(response.data); // ✅ Axios gives data directly
    } catch (error) {
      console.error("Fetch event error:", error);
      setError(error.response?.data?.message || "Failed to load event data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            navigate("/"); // redirect when countdown finishes
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [success, navigate]);

  if (success) {
    return (
      <div className="form-container text-center">
        <h1 className="text-3xl font-bold text-green-600 mb-4">
          ✅ Registration Completed
        </h1>
        <p className="text-gray-700 mb-4">
          Thank you for registering! Your information has been submitted
          successfully. You’re all set — no further action is required.
        </p>
        <p className="text-gray-600">
          You will be redirected to the login page in{" "}
          <span className="font-semibold">{countdown}</span> seconds...
        </p>
      </div>
    );
  }

  // Only groups the backend flags with showForms sign consent forms
  const showConsentForms = (eventData?.timeSlots?.[selectedDate] || []).some(
    (slot) => slot.label === selectedGroup && slot.showForms
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep1 = (data) => {
    if (!selectedGroup) return "Please select a time slot.";
    if (!data.firstName.trim()) return "First name is required.";
    if (!data.lastName.trim()) return "Last name is required.";
    if (!data.dateOfBirth) return "Date of birth is required.";
    if (!data.sex) return "Sex is required.";
    if (!data.streetAddress.trim()) return "Street address is required.";
    if (!data.city.trim()) return "City is required.";
    if (!data.state || data.state.trim().length !== 2)
      return "State is required (2-letter code).";
    if (!data.zipCode.trim()) return "Zip code is required.";
    if (!data.phoneNumber.trim()) return "Phone number is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) return "A valid email is required.";
    return null;
  };

  const validateStep2 = (data) => {
    if (!data.policyHolderFirstName?.trim())
      return "Policy Holder First Name is required.";
    if (!data.policyHolderLastName?.trim())
      return "Policy Holder Last Name is required.";
    if (!data.policyHolderAddress?.trim())
      return "Policy Holder Address is required.";
    if (!data.entityType?.trim()) return "Entity Type is required.";
    if (!data.policyHolderSex?.trim()) return "Policy Holder Sex is required.";
    if (!data.insuranceName?.trim()) return "Insurance Name is required.";
    if (!data.relationshipToPolicyHolder?.trim())
      return "Relationship to Policy Holder is required.";
    if (!data.memberID?.trim())
      return "Member ID/Certification Number is required.";
    if (!data.policyGroupNumber?.trim())
      return "Policy/Group Number is required.";
    if (!data.issueDate) return "Issue Date is required.";
    if (!data.expiration) return "Expiration Date is required.";
    if (!insuranceCardFiles.front)
      return "Front insurance card image is required.";
    if (!insuranceCardFiles.back) return "Back insurance card image is required.";
    return null; // no errors
  };

  const validateConsents = () => {
    for (const { formType, title } of CONSENT_FORMS) {
      if (!consents[formType].agreed)
        return `Please confirm you have read and agree to the ${title}.`;
      if (!consents[formType].signature)
        return `Please sign the ${title}.`;
    }
    return null;
  };

  const handleConsentChange = (formType, field, value) => {
    setConsents((prev) => ({
      ...prev,
      [formType]: { ...prev[formType], [field]: value },
    }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    const validationError = validateStep1(formData);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setStep(2);
  };

  const handleBack = () => {
    setError("");
    setStep((currentStep) => Math.max(1, currentStep - 1));
  };

  const handleInsuranceCardFileChange = (side, file) => {
    if (!file) {
      setInsuranceCardFiles((prev) => ({ ...prev, [side]: null }));
      return;
    }

    if (!ALLOWED_INSURANCE_CARD_FILE_TYPES.includes(file.type)) {
      setError("Insurance card files must be JPEG, PNG, or WEBP images.");
      return;
    }

    if (file.size > MAX_INSURANCE_CARD_FILE_SIZE_BYTES) {
      setError("Insurance card files must be 25MB or smaller.");
      return;
    }

    setError("");
    setInsuranceCardFiles((prev) => ({ ...prev, [side]: file }));
  };

  const formatPhoneNumber = (value) => {
    const digits = String(value).replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 9)
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
    // 10 digits -> show last part as 4 digits
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };

  // ✅ Step 2 -> Step 3 (Review)
  const handleStep2Next = (e) => {
    e.preventDefault();
    setError("");

    const validationError = validateStep2(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    setStep(3); // only go to review, don't submit
  };

  // Enter key submits the form, so route it to the current step's action
  const handleFormSubmit = (e) => {
    if (step === 1) return handleNext(e);
    if (step === 2) return handleStep2Next(e);
    if (step === 3) return handleReviewSubmit(e);
    return handleSubmit(e);
  };

  const handleReviewNext = () => {
    setError("");
    setStep(4);
  };

  const handleReviewSubmit = (e) => {
    if (showConsentForms) {
      e.preventDefault();
      return handleReviewNext();
    }
    return handleSubmit(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const validationError =
      validateStep2(formData) || (showConsentForms && validateConsents());
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const [frontCardDataUrl, backCardDataUrl] = await Promise.all([
        compressImageToDataUrl(insuranceCardFiles.front),
        compressImageToDataUrl(insuranceCardFiles.back),
      ]);

      const submissionData = {
        timeSlot: selectedGroup,
        ...formData,
        createdBy: user?.id || user?.email || null,
        insuranceCardFront: {
          dataUrl: frontCardDataUrl,
          originalFileName: insuranceCardFiles.front.name,
        },
        insuranceCardBack: {
          dataUrl: backCardDataUrl,
          originalFileName: insuranceCardFiles.back.name,
        },
        consentSignatures: showConsentForms ? consents : undefined,
      };

      await api.post("/patients", submissionData, {
        timeout: SUBMIT_TIMEOUT_MS,
      });

      setSuccess(true);
      setSelectedGroup("");
      setFormData({ ...initialFormData });
      setInsuranceCardFiles({ ...initialInsuranceCardFiles });
      setConsents(createInitialConsents());
      setStep(1);
      setCountdown(10); // 🔹 reset countdown for success screen
    } catch (err) {
      console.error("Submission error:", err);
      setError(getSubmissionErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="form-container text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          ✅ Registration Completed
        </h1>
        <p className="text-gray-600 mb-4">
          Thank you for registering! You will be redirected to the login page
          shortly.
        </p>
        <p className="text-gray-600">
          You will be redirected to the login page in{" "}
          <span className="font-semibold">{countdown}</span> seconds...
        </p>
      </div>
    );
  }

  return (
    <div className="form-container">
      <Group className="faq-floating-group">
        <Button onClick={() => setFaqModalOpened(true)} className="faq-button">
          <IconHelp size={24} />
        </Button>

        <Text size="sm" className="faq-label">
          Got questions? Check FAQ
        </Text>
      </Group>

      {/* Title Section */}
      <div className="event-title-section">
        <Stack spacing="sm" className="event-title-stack">
          <Title order={1} className="event-title">
            AIRs Registration
          </Title>

          {step === 1 && eventData?.event && (
            <Text className="event-subtitle">{eventData?.event}</Text>
          )}
        </Stack>
      </div>
      <form onSubmit={handleFormSubmit}>
        {/* Time Slot */}
        {step === 1 && (
          <div>
            {/* Date Selector */}
            <div className="form-group">
              <label>
                Select Date <span aria-hidden="true">*</span>
              </label>
              <select
                className="form-control"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedGroup(""); // reset when date changes
                }}
                required
              >
                <option value="">Choose a date...</option>
                {Object.keys(eventData?.timeSlots || {}).map((date) => (
                  <option key={date} value={date}>
                    {new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </option>
                ))}
              </select>
            </div>

            {/* Time Slot Selector */}
            <div className="form-group">
              <label>
                Select Time Slot & Group <span aria-hidden="true">*</span>
              </label>
              <select
                className="form-control"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={!selectedDate}
                required
              >
                <option value="">Choose your time slot...</option>
                {(eventData?.timeSlots?.[selectedDate] || []).map((slot) => (
                  <option key={slot.label} value={slot.label}>
                    {slot.label}
                  </option>
                ))}
              </select>

              {/* ✅ Mantine Alert for students */}
              {/* {selectedGroup.includes("School District Staff & Students") && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Notice"
                  color="red"
                  radius="md"
                  mt="md"
                >
                  Students must be <strong>16+ years old</strong> to sign up for
                  this event.
                </Alert>
              )} */}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Insurance Information
            </h2>
            <p className="text-gray-600">
              Please enter your insurance information exactly as it appears on
              your insurance card.
            </p>
          </div>
        )}

        {selectedGroup && step === 1 && (
          <>
            {/* --- Step 1: Personal Info + Address + Organization --- */}
            <div className="form-section">
              <h3>Basic Personal Information</h3>
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Middle Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.middleName}
                    onChange={(e) =>
                      handleInputChange("middleName", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.dateOfBirth}
                    onChange={(e) =>
                      handleInputChange("dateOfBirth", e.target.value)
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SSN</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.ssn}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // remove non-digits

                      // limit to 9 digits
                      if (value.length > 9) value = value.slice(0, 9);

                      // format as XXX-XX-XXXX
                      let formatted = value;
                      if (value.length > 5) {
                        formatted =
                          value.slice(0, 3) +
                          "-" +
                          value.slice(3, 5) +
                          "-" +
                          value.slice(5);
                      } else if (value.length > 3) {
                        formatted = value.slice(0, 3) + "-" + value.slice(3);
                      }

                      handleInputChange("ssn", formatted);
                    }}
                    placeholder="XXX-XX-XXXX"
                    maxLength={11} // includes dashes
                  />
                </div>

                <div className="form-group">
                  <label>Sex *</label>
                  <select
                    className="form-control"
                    value={formData.sex}
                    onChange={(e) => handleInputChange("sex", e.target.value)}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                  </select>
                </div>
              </div>

              <div className="form-grid form-grid-2">
                {/* Race */}
                <div className="form-group">
                  <label>Race *</label>
                  <select
                    className="form-control"
                    value={formData.race}
                    onChange={(e) => handleInputChange("race", e.target.value)}
                    disabled={formData.raceDeclined}
                    required={!formData.raceDeclined} // required only if not declined
                  >
                    <option value="">Select...</option>
                    {RACE_OPTIONS.map((race) => (
                      <option key={race} value={race}>
                        {race}
                      </option>
                    ))}
                  </select>
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      checked={formData.raceDeclined}
                      onChange={(e) => {
                        handleInputChange("raceDeclined", e.target.checked);
                        if (e.target.checked) handleInputChange("race", "");
                      }}
                    />
                    <label>Declined</label>
                  </div>
                </div>

                {/* Ethnicity */}
                <div className="form-group">
                  <label>Ethnicity *</label>
                  <select
                    className="form-control"
                    value={formData.ethnicity}
                    onChange={(e) =>
                      handleInputChange("ethnicity", e.target.value)
                    }
                    disabled={formData.ethnicityDeclined}
                    required={!formData.ethnicityDeclined} // required only if not declined
                  >
                    <option value="">Select...</option>
                    {ETHNICITY_OPTIONS.map((ethnicity) => (
                      <option key={ethnicity} value={ethnicity}>
                        {ethnicity}
                      </option>
                    ))}
                  </select>
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      checked={formData.ethnicityDeclined}
                      onChange={(e) => {
                        handleInputChange(
                          "ethnicityDeclined",
                          e.target.checked
                        );
                        if (e.target.checked)
                          handleInputChange("ethnicity", "");
                      }}
                    />
                    <label>Declined</label>
                  </div>
                </div>
              </div>

              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Language</label>
                  <select
                    className="form-control"
                    value={formData.language}
                    onChange={(e) =>
                      handleInputChange("language", e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    {LANGUAGE_OPTIONS.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Marital Status</label>
                  <select
                    className="form-control"
                    value={formData.maritalStatus}
                    onChange={(e) =>
                      handleInputChange("maritalStatus", e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    {MARITAL_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="form-section">
              <h3>Contact Information</h3>

              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    className="form-control"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleInputChange(
                        "phoneNumber",
                        formatPhoneNumber(e.target.value)
                      )
                    }
                    placeholder="(XXX) XXX XXX"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.streetAddress}
                  onChange={(e) =>
                    handleInputChange("streetAddress", e.target.value)
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Street Address 2</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.streetAddress2}
                  onChange={(e) =>
                    handleInputChange("streetAddress2", e.target.value)
                  }
                />
              </div>

              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.state}
                    onChange={(e) =>
                      handleInputChange("state", e.target.value.toUpperCase())
                    }
                    maxLength={2}
                    placeholder="MN"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.zipCode}
                    onChange={(e) =>
                      handleInputChange("zipCode", e.target.value)
                    }
                    maxLength={10}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Organization */}
            {/* <div className="form-section">
              <h3>Organization Information</h3>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.organization}
                    onChange={(e) =>
                      handleInputChange("organization", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Title/Role</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.titleRole}
                    onChange={(e) =>
                      handleInputChange("titleRole", e.target.value)
                    }
                  />
                </div>
              </div>
            </div> */}

            <div className="text-center mt-6">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ minWidth: "200px" }}
              >
                Next: Insurance Policy Information
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* --- Step 2:  Insurance Policy Holder + Insurance Info Policy Info  --- */}
            <div className="form-section">
              <h3>Insurance Policy Holder Information</h3>

              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Policy Holder First Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.policyHolderFirstName}
                    onChange={(e) =>
                      handleInputChange("policyHolderFirstName", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Policy Holder Last Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.policyHolderLastName}
                    onChange={(e) =>
                      handleInputChange("policyHolderLastName", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Policy Holder Address *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.policyHolderAddress}
                  onChange={(e) =>
                    handleInputChange("policyHolderAddress", e.target.value)
                  }
                />
              </div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Entity Type *</label>
                  <select
                    className="form-control"
                    value={formData.entityType}
                    onChange={(e) =>
                      handleInputChange("entityType", e.target.value)
                    }
                  >
                    <option value="person">Person</option>
                    <option value="non-person entity">Non-person entity</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Policy Holder Sex *</label>
                  <select
                    className="form-control"
                    value={formData.policyHolderSex}
                    onChange={(e) =>
                      handleInputChange("policyHolderSex", e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="form-section">
              <h3>Insurance Policy Information</h3>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Insurance Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.insuranceName}
                    onChange={(e) =>
                      handleInputChange("insuranceName", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Relationship to Policy Holder *</label>
                  <select
                    className="form-control"
                    value={formData.relationshipToPolicyHolder}
                    onChange={(e) =>
                      handleInputChange(
                        "relationshipToPolicyHolder",
                        e.target.value
                      )
                    }
                  >
                    <option value="">Select...</option>
                    {RELATIONSHIP_OPTIONS.map((relationship) => (
                      <option key={relationship} value={relationship}>
                        {relationship}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Member ID/Certification Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.memberID}
                    onChange={(e) =>
                      handleInputChange("memberID", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Policy/Group Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.policyGroupNumber}
                    onChange={(e) =>
                      handleInputChange("policyGroupNumber", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="form-grid form-grid-2">
                {/* Issue Date */}
                <div className="form-group">
                  <label>Issue Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.issueDate}
                    onChange={(e) =>
                      handleInputChange("issueDate", e.target.value)
                    }
                  />
                </div>

                {/* Expiration Date */}
                <div className="form-group">
                  <label>Expiration *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.expiration}
                    onChange={(e) =>
                      handleInputChange("expiration", e.target.value)
                    }
                    min={formData.issueDate || ""} // sets minimum selectable date
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Insurance Card Images</h3>
              <p className="text-gray-600 mb-4">
                Upload a clear front and back photo of the insurance card. Each
                image must be JPEG, PNG, or WEBP and 25MB or smaller.
              </p>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label>Front of Insurance Card *</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) =>
                      handleInsuranceCardFileChange(
                        "front",
                        e.target.files?.[0] || null
                      )
                    }
                  />
                  {insuranceCardFiles.front && (
                    <Text size="sm" c="dimmed" mt="xs">
                      Selected: {insuranceCardFiles.front.name}
                    </Text>
                  )}
                </div>
                <div className="form-group">
                  <label>Back of Insurance Card *</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) =>
                      handleInsuranceCardFileChange(
                        "back",
                        e.target.files?.[0] || null
                      )
                    }
                  />
                  {insuranceCardFiles.back && (
                    <Text size="sm" c="dimmed" mt="xs">
                      Selected: {insuranceCardFiles.back.name}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center mt-6">
              <Group spacing="md">
                <button
                  type="button"
                  className="btn btn-secondary mr-4"
                  onClick={handleBack}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStep2Next}
                >
                  Review Your Information
                </button>
              </Group>
            </div>
          </>
        )}
        {step === 3 && (
          <Stack spacing="xl" maw={800} mx="auto">
            <Title order={2} align="center">
              Review Your Information
            </Title>
            <Text c="dimmed" align="center">
              Please confirm the details below before submitting your
              registration.
            </Text>

            {/* Personal Information */}
            <Card withBorder shadow="sm" radius="md" p="lg">
              <Title order={4} mb="sm">
                Personal Information
              </Title>
              <Divider mb="md" />
              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text fw={700} c="blue.7">
                    Full Name
                  </Text>
                  <Text>
                    {formData.firstName ||
                    formData.middleName ||
                    formData.lastName ? (
                      `${formData.firstName} ${formData.middleName} ${formData.lastName}`
                    ) : (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Date of Birth
                  </Text>
                  <Text>
                    {formData.dateOfBirth || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Sex
                  </Text>
                  <Text>
                    {formData.sex || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    SSN
                  </Text>
                  <Text>
                    {formData.ssn || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
              </SimpleGrid>
            </Card>

            {/* Address & Contact Information */}
            <Card withBorder shadow="sm" radius="md" p="lg">
              <Title order={4} mb="sm">
                Address & Contact Information
              </Title>
              <Divider mb="md" />

              <Text>
                {formData.streetAddress || <Text c="dimmed">Not provided</Text>}
                {", "}
                {formData.streetAddress2 || ""}
              </Text>
              <Stack mb="sm" spacing="xs">
                <Text>
                  {formData.city || <Text c="dimmed">Not provided</Text>},{" "}
                  {formData.state || <Text c="dimmed">Not provided</Text>}{" "}
                  {formData.zipCode || <Text c="dimmed">Not provided</Text>}
                </Text>

                <div>
                  <Text fw={700} c="blue.7">
                    Phone
                  </Text>
                  <Text>
                    {formData.phoneNumber || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Email
                  </Text>
                  <Text>
                    {formData.email || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
              </Stack>
            </Card>

            {/* Demographics */}
            <Card withBorder shadow="sm" radius="md" p="lg">
              <Title order={4} mb="sm">
                Demographic Information
              </Title>
              <Divider mb="md" />
              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text fw={700} c="blue.7">
                    Race
                  </Text>
                  <Text>
                    {formData.race || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Ethnicity
                  </Text>
                  <Text>
                    {formData.ethnicity || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Language
                  </Text>
                  <Text>
                    {formData.language || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Marital Status
                  </Text>
                  <Text>
                    {formData.maritalStatus || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
              </SimpleGrid>
            </Card>

            {/* Organization */}
            <Card withBorder shadow="sm" radius="md" p="lg">
              <Title order={4} mb="sm">
                Organization Information
              </Title>
              <Divider mb="md" />
              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text fw={700} c="blue.7">
                    Organization
                  </Text>
                  <Text>
                    {formData.organization || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Title/Role
                  </Text>
                  <Text>
                    {formData.titleRole || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
              </SimpleGrid>
            </Card>

            {/* Insurance Policy Holder Details */}
            <Card withBorder shadow="sm" radius="md" p="lg" mb="md">
              <Title order={4} mb="sm">
                Insurance Policy Holder Details
              </Title>
              <Divider mb="md" />
              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text fw={700} c="blue.7">
                    Policy Holder Name
                  </Text>
                  <Text>
                    {formData.policyHolderFirstName &&
                    formData.policyHolderLastName ? (
                      `${formData.policyHolderFirstName} ${formData.policyHolderLastName}`
                    ) : (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Entity Type
                  </Text>
                  <Text>
                    {formData.entityType || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Policy Holder Address
                  </Text>
                  <Text>
                    {formData.policyHolderAddress || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Policy Holder Sex
                  </Text>
                  <Text>
                    {formData.policyHolderSex || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Relationship to Policy Holder
                  </Text>
                  <Text>
                    {formData.relationshipToPolicyHolder || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
              </SimpleGrid>
            </Card>

            {/* Insurance Details */}
            <Card withBorder shadow="sm" radius="md" p="lg">
              <Title order={4} mb="sm">
                Insurance Policy Details
              </Title>
              <Divider mb="md" />
              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text fw={700} c="blue.7">
                    Insurance Name
                  </Text>
                  <Text>
                    {formData.insuranceName || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Member ID
                  </Text>
                  <Text>
                    {formData.memberID || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Group Number
                  </Text>
                  <Text>
                    {formData.policyGroupNumber || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Issue Date
                  </Text>
                  <Text>
                    {formData.issueDate || <Text c="dimmed">Not provided</Text>}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Expiration Date
                  </Text>
                  <Text>
                    {formData.expiration || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
              </SimpleGrid>
            </Card>

            <Card withBorder shadow="sm" radius="md" p="lg">
              <Title order={4} mb="sm">
                Insurance Card Uploads
              </Title>
              <Divider mb="md" />
              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text fw={700} c="blue.7">
                    Front Image
                  </Text>
                  <Text>
                    {insuranceCardFiles.front?.name || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
                <div>
                  <Text fw={700} c="blue.7">
                    Back Image
                  </Text>
                  <Text>
                    {insuranceCardFiles.back?.name || (
                      <Text c="dimmed">Not provided</Text>
                    )}
                  </Text>
                </div>
              </SimpleGrid>
              <Text size="sm" c="dimmed" mt="md">
                These encrypted images are retained for 30 days and then
                removed automatically.
              </Text>
            </Card>

            {/* Buttons */}
            <div className="text-center mt-6">
              <Group spacing="md">
                <button
                  type="button"
                  className="btn btn-secondary mr-4"
                  onClick={() => setStep(2)}
                >
                  Back
                </button>
                {showConsentForms ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleReviewNext}
                  >
                    Next: Sign Consent Forms
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Registration"}
                  </button>
                )}
              </Group>
            </div>
          </Stack>
        )}

        {step === 4 && showConsentForms && (
          <Stack spacing="xl" maw={800} mx="auto">
            <Title order={2} align="center">
              Sign Consent Forms
            </Title>
            <Text c="dimmed" align="center">
              Please read each form below, confirm you agree, and click the
              highlighted box on the signature line to sign. Your details and
              today's date have been filled in for you.
            </Text>

            {needsGuardianSignature && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Parent or guardian signature required"
                color="orange"
                radius="md"
              >
                Because the patient is under {CONSENT_GUARDIAN_AGE}, these
                forms must be reviewed, agreed to, and signed by their{" "}
                <strong>parent or legal guardian</strong>, not by the patient.
              </Alert>
            )}

            {CONSENT_FORMS.map(({ formType, title }) => (
              <Card key={formType} withBorder shadow="sm" radius="md" p="lg">
                <Title order={4} mb="sm">
                  {title}
                </Title>
                <Divider mb="md" />
                <Suspense fallback={<Loader size="sm" mx="auto" my="xl" />}>
                  <ConsentPdfSigner
                    title={title}
                    templateUrl={getConsentTemplateUrl(formType)}
                    fieldValues={consentFieldValues}
                    signature={consents[formType].signature}
                    onSign={(signature) =>
                      handleConsentChange(formType, "signature", signature)
                    }
                  />
                </Suspense>
                <Text size="sm" mt="xs">
                  <a
                    href={getConsentTemplateUrl(formType)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open blank {title} in a new tab
                  </a>
                </Text>

                <div className="checkbox-group consent-agree">
                  <input
                    id={`consent-agree-${formType}`}
                    type="checkbox"
                    checked={consents[formType].agreed}
                    onChange={(e) =>
                      handleConsentChange(formType, "agreed", e.target.checked)
                    }
                  />
                  <label htmlFor={`consent-agree-${formType}`}>
                    {needsGuardianSignature
                      ? `As the patient's parent or legal guardian, I have read and agree to the ${title}.`
                      : `I have read and agree to the ${title}.`}
                  </label>
                </div>

                <Text
                  size="sm"
                  mt="xs"
                  fw={600}
                  c={consents[formType].signature ? "green.7" : "orange.7"}
                >
                  {consents[formType].signature
                    ? "✓ Signed"
                    : "Not signed yet: click the highlighted box on the form to sign."}
                </Text>
              </Card>
            ))}

            {/* Buttons */}
            <div className="text-center mt-6">
              <Group spacing="md">
                <button
                  type="button"
                  className="btn btn-secondary mr-4"
                  onClick={() => {
                    setError("");
                    setStep(3);
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Sign & Submit Registration"}
                </button>
              </Group>
            </div>
          </Stack>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mt-6 text-center">
            <Stack>
              <strong>❌ {error}</strong>
            </Stack>
          </div>
        )}
      </form>
      <FaqModal
        opened={faqModalOpened}
        onClose={() => setFaqModalOpened(false)}
      />
    </div>
  );
};

export default PatientForm;
